import { useEffect, useState } from 'react';

export type MapLatLng = { lat: number; lng: number };

/** Last-resort only — never the primary map default. */
export const LAST_RESORT_MAP_CENTER: MapLatLng = { lat: 12.9716, lng: 77.5946 };

const STORAGE_KEY = 'mapLastKnownCenter';

const COUNTRY_CENTERS: Record<string, MapLatLng> = {
  IN: { lat: 20.5937, lng: 78.9629 },
  US: { lat: 39.8283, lng: -98.5795 },
  GB: { lat: 54.7024, lng: -3.2766 },
  AE: { lat: 23.4241, lng: 53.8478 },
  SG: { lat: 1.3521, lng: 103.8198 },
  AU: { lat: -25.2744, lng: 133.7751 },
  CA: { lat: 56.1304, lng: -106.3468 },
  DE: { lat: 51.1657, lng: 10.4515 },
  FR: { lat: 46.2276, lng: 2.2137 },
  JP: { lat: 36.2048, lng: 138.2529 },
};

const TZ_TO_COUNTRY: Record<string, string> = {
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'Europe/London': 'GB',
  'Asia/Dubai': 'AE',
  'Asia/Singapore': 'SG',
  'Australia/Sydney': 'AU',
  'America/Toronto': 'CA',
  'Europe/Berlin': 'DE',
  'Europe/Paris': 'FR',
  'Asia/Tokyo': 'JP',
};

export function isValidMapCenter(c?: MapLatLng | null): c is MapLatLng {
  return Boolean(
    c
    && Number.isFinite(c.lat)
    && Number.isFinite(c.lng)
    && Math.abs(c.lat) <= 90
    && Math.abs(c.lng) <= 180,
  );
}

export function peekCachedMapCenter(): MapLatLng | null {
  if (typeof window === 'undefined') return null;
  for (const store of [sessionStorage, localStorage]) {
    try {
      const raw = store.getItem(STORAGE_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { lat?: number; lng?: number };
      if (isValidMapCenter(parsed)) return { lat: parsed.lat, lng: parsed.lng };
    } catch {
      /* ignore */
    }
  }
  return null;
}

export function cacheMapCenter(c: MapLatLng) {
  if (!isValidMapCenter(c) || typeof window === 'undefined') return;
  const raw = JSON.stringify({ lat: c.lat, lng: c.lng, at: Date.now() });
  try { sessionStorage.setItem(STORAGE_KEY, raw); } catch { /* ignore */ }
  try { localStorage.setItem(STORAGE_KEY, raw); } catch { /* ignore */ }
}

function countryCenterFromDevice(): MapLatLng | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const code = TZ_TO_COUNTRY[tz];
    if (code && COUNTRY_CENTERS[code]) return COUNTRY_CENTERS[code];
  } catch {
    /* ignore */
  }
  return null;
}

export function requestBrowserPosition(timeoutMs = 8000): Promise<MapLatLng> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(new Error(err.message || 'Unable to get location')),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}

/**
 * GPS first, then last-known cache, caller fallback, device country, Bangalore last.
 */
export async function getCurrentMapCenter(options?: {
  fallback?: MapLatLng | null;
  timeoutMs?: number;
}): Promise<MapLatLng> {
  try {
    const gps = await requestBrowserPosition(options?.timeoutMs ?? 8000);
    cacheMapCenter(gps);
    return gps;
  } catch {
    const cached = peekCachedMapCenter();
    if (cached) return cached;
    if (isValidMapCenter(options?.fallback)) return options.fallback;
    const country = countryCenterFromDevice();
    if (country) return country;
    return LAST_RESORT_MAP_CENTER;
  }
}

/** Immediate cache/preferred center so maps never flash a world view first. */
export function useMapCenter(preferred?: MapLatLng | null): {
  center: MapLatLng | null;
  ready: boolean;
} {
  const cached = peekCachedMapCenter();
  const initial = isValidMapCenter(preferred) ? preferred : cached;
  const [center, setCenter] = useState<MapLatLng | null>(initial);

  useEffect(() => {
    if (isValidMapCenter(preferred)) {
      setCenter(preferred);
      return;
    }
    let cancelled = false;
    void getCurrentMapCenter({ fallback: preferred }).then((next) => {
      if (!cancelled) setCenter(next);
    });
    return () => { cancelled = true; };
  }, [preferred?.lat, preferred?.lng]);

  return { center, ready: isValidMapCenter(center) };
}
